import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Socket } from "phoenix";

import packageJson from "../../package.json";

import type { Channel } from "phoenix";
import type { JSX } from "react";

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export type Channels = Record<string, Channel>;

export enum WebSocketConnectionStatus {
  Uninstantiated = "uninstantiated",
  Connecting = "connecting",
  Open = "open",
  Error = "error",
  Closed = "closed",
}

interface PushMessage<T, E = unknown> {
  channelTopic: string;
  event: string;
  payload?: Record<string, unknown>;
  onSuccess?: (response: T) => void;
  onError?: (response: E) => void;
}

export interface JoinChannelParams<T = unknown> {
  channelTopic: string;
  channelParams?: Record<string, unknown>;
  onChannelJoinSuccess?: (response: T) => void;
}

type PushMessageFunction = <T, E = unknown>({
  channelTopic,
  event,
  payload,
  onSuccess,
  onError,
}: PushMessage<T, E>) => void;

type JoinChannelFunction = <T = unknown>({
  channelTopic,
  channelParams,
  onChannelJoinSuccess,
}: JoinChannelParams<T>) => void;

interface IWebSocketContext {
  channels: Channels | null;
  status: WebSocketConnectionStatus;
  joinChannel: JoinChannelFunction;
  leaveChannel: (channelTopic: string) => void;
  pushMessage: PushMessageFunction;
  disconnect: () => void;
}

export const WebSocketContext = createContext<IWebSocketContext>({
  channels: null,
  status: WebSocketConnectionStatus.Uninstantiated,
  joinChannel: () => {},
  leaveChannel: () => {},
  pushMessage: () => {},
  disconnect: () => {},
});

export function WebSocketProvider({
  children,
}: WebSocketProviderProps): JSX.Element {
  const SOCKET_URL = process.env.SOCKET_URL ?? "";
  const {
    localStorage: { token },
  } = global.window || {};

  const [channels, setChannels] = useState<Channels | null>(null);
  const [joiningList, setJoiningList] = useState<string[]>([]);
  const [status, setStatus] = useState<WebSocketConnectionStatus>(
    WebSocketConnectionStatus.Connecting,
  );

  const socketRef = useRef<Socket | null>(null);
  const pendingJoinsRef = useRef<JoinChannelParams[]>([]);
  const doJoin = useCallback(
    (
      socket: Socket,
      { channelTopic, channelParams, onChannelJoinSuccess }: JoinChannelParams,
    ) => {
      setJoiningList((prev) => {
        if (prev.includes(channelTopic)) return prev;
        return [...prev, channelTopic];
      });

      const newChannel = socket.channel(channelTopic, channelParams);

      newChannel
        .join()
        .receive("ok", (response) => {
          setChannels((prev) => ({
            ...(prev ?? {}),
            [channelTopic]: newChannel,
          }));
          setJoiningList((prev) =>
            prev.filter((topic) => topic !== channelTopic),
          );
          onChannelJoinSuccess?.(response);
        })
        .receive("error", (response) => {
          console.error(
            `Error joining channel ${channelTopic}:`,
            response.reason,
          );
          setStatus(WebSocketConnectionStatus.Error);
          setJoiningList((prev) =>
            prev.filter((topic) => topic !== channelTopic),
          );
        });
    },
    [],
  );

  useEffect(() => {
    if (!token || !SOCKET_URL) return undefined;

    const active = { current: true };

    const socket = new Socket(SOCKET_URL, {
      params: {
        token,
        "x-app-version": packageJson.version,
      },
    });

    socketRef.current = socket;

    socket.onOpen(() => {
      if (!active.current) return;
      setStatus(WebSocketConnectionStatus.Open);
      const pending = [...pendingJoinsRef.current];
      pendingJoinsRef.current = [];
      pending.forEach((params) => doJoin(socket, params));
    });

    socket.onError(() => {
      if (active.current) setStatus(WebSocketConnectionStatus.Error);
    });

    socket.onClose(() => {
      if (active.current) setStatus(WebSocketConnectionStatus.Closed);
    });

    socket.connect();

    return () => {
      active.current = false;
      pendingJoinsRef.current = [];
      socketRef.current = null;
      setChannels(null);
      setJoiningList([]);
      setStatus(WebSocketConnectionStatus.Connecting);
      socket.disconnect();
    };
  }, [token, SOCKET_URL, doJoin]);

  const joinChannel: JoinChannelFunction = useCallback(
    ({ channelTopic, channelParams, onChannelJoinSuccess }) => {
      const socket = socketRef.current;
      const existingChannel = channels?.[channelTopic];
      const isJoining = joiningList.includes(channelTopic);

      if (isJoining || existingChannel) return;

      if (!socket) {
        const alreadyPending = pendingJoinsRef.current.some(
          (p) => p.channelTopic === channelTopic,
        );
        if (!alreadyPending) {
          pendingJoinsRef.current.push({
            channelTopic,
            channelParams,
            onChannelJoinSuccess,
          } as JoinChannelParams);
        }
        return;
      }

      doJoin(socket, {
        channelTopic,
        channelParams,
        onChannelJoinSuccess,
      } as JoinChannelParams);
    },
    [channels, joiningList, doJoin],
  );

  const pushMessage: PushMessageFunction = useCallback(
    ({ channelTopic, event, payload = {}, onSuccess, onError }) => {
      const channel = channels?.[channelTopic];

      if (!channel) return;

      channel
        .push(event, payload)
        .receive("ok", (response) => {
          onSuccess?.(response);
        })
        .receive("error", (response) => {
          onError?.(response);
        });
    },
    [channels],
  );

  const leaveChannel = useCallback(
    (channelTopic: string) => {
      const existingChannel = channels?.[channelTopic];

      if (!existingChannel) return;

      existingChannel
        .leave()
        .receive("ok", () => {
          setChannels((prev) =>
            prev
              ? Object.fromEntries(
                  Object.entries(prev).filter(([key]) => key !== channelTopic),
                )
              : null,
          );
        })
        .receive("error", (response) => {
          console.error("Error leaving WS:", response.reason);
        });
    },
    [channels],
  );

  const disconnect = useCallback(() => {
    socketRef.current?.disconnect();
  }, []);

  const value = useMemo(
    () => ({
      status,
      channels,
      joinChannel,
      leaveChannel,
      pushMessage,
      disconnect,
    }),
    [status, channels, joinChannel, leaveChannel, pushMessage, disconnect],
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
